/**
 * validateLaunch — the PRE-DISPATCH gate for the reshaped mortgagee launch.
 *
 * Every rule here refuses a submit BEFORE any launch call is made (AC-3): a
 * refusal must cost the AM a correction, not a cancelled run. The rules are pure
 * functions over the binding and the split values, so they are asserted without
 * rendering anything.
 *
 * Pure: no React, no Office.js, no network.
 */

import { mortgageeAddressError } from "~/common/workflows/launch/addressParts";
import {
  readPolicyOptions,
  readPolicyTermKey,
  validateEffectiveDateAgainstPolicy,
  validatePolicyMembership,
} from "~/common/workflows/launch/policyPrefillOptions";
import {
  actionFields,
  mergeLaunchSlice,
  type LaunchBinding,
} from "~/common/workflows/launch/launchSchemaBinding";
import {
  checkAllRequiredFilled,
  type FieldSchema,
} from "~/common/workflows/schemaHelpers";
import type { PolicyInForce } from "~/common/workflows/types/client";

export interface ValidateLaunchArgs {
  binding: LaunchBinding;
  runLevelValues: Record<string, unknown>;
  actions: Record<string, unknown>[];
  /**
   * The in-force option rows, when the caller already holds them. Omitted, they
   * are read from the merged slice's freeform `policy_options` echo — which is
   * where the drawer keeps them.
   */
  policyOptions?: readonly PolicyInForce[];
}

export interface LaunchValidation {
  /** Run-level field key → message. */
  fieldErrors: Record<string, string>;
  /** Parallel to `actions`: declared action field key → message. */
  actionErrors: Array<Record<string, string>>;
  /**
   * Non-null when the submit is BLOCKED. Carries the form-level reason; the
   * per-field messages carry the detail.
   */
  blockingReason: string | null;
}

/**
 * Are the rows the AM actually entered complete?
 *
 * `checkAllRequiredFilled` descends into an `array_of_objects` field's item
 * schema only when the ARRAY ITSELF is required — which is right for a renderer
 * gate: an optional list may legitimately be empty. But the reshaped contract
 * declares `interests` OPTIONAL while its item schema requires `name`, so a row
 * the AM added and left half-filled passes the renderer's gate and is refused by
 * the substrate instead. That is a silent gate loss the grouped form inherits by
 * design, because it is the form that nests required fields.
 *
 * This closes it WITHOUT changing the shared helper (whose behaviour must stay
 * byte-identical for `JsonSchemaForm`'s two hosts): an empty optional list still
 * passes; a row that EXISTS must satisfy its own required list, checked with the
 * same helper so the two gates cannot drift apart.
 */
function presentRowsAreComplete(
  fields: FieldSchema[],
  action: Record<string, unknown>,
): boolean {
  for (const field of fields) {
    if (field.type !== "array_of_objects" || field.required) continue;
    const itemFields = field.itemFields ?? [];
    if (itemFields.length === 0) continue;
    const rows = action[field.key];
    if (!Array.isArray(rows)) continue;
    for (const row of rows) {
      if (!row || typeof row !== "object" || Array.isArray(row)) return false;
      if (!checkAllRequiredFilled(itemFields, row as Record<string, unknown>)) {
        return false;
      }
    }
  }
  return true;
}

function firstMessage(
  actionErrors: Array<Record<string, string>>,
): string | null {
  for (const errors of actionErrors) {
    for (const message of Object.values(errors)) {
      if (message) return message;
    }
  }
  return null;
}

/**
 * Validate a reshaped launch.
 *
 * Rules:
 *  (p) the policy number must be one of the client's resolved policies, with
 *      `validatePolicyMembership`'s wording, the same sentence the launch-time
 *      backstop (`resolveLaunchTerm`) gives. First, so it is the reason shown:
 *      a date checked against a policy that isn't the client's says nothing;
 *  (a) the effective date must fall inside the CHOSEN TERM, with the existing
 *      helper's wording — it names the term, not just the policy number (AC-3);
 *  (b) interest type, WHEN PRESENT, must be one of the declared enumeration. It
 *      is pre-filled with the contract's default and therefore never blocks
 *      (AC-4 as amended by the operator, 2026-08-26); what survives is a
 *      membership check, so a value outside the declared enumeration — a
 *      contract that grew ahead of the form, or a devtools edit — is still
 *      refused;
 *  (c) the action slice's declared REQUIRED fields must be filled, using the
 *      SAME helper the renderer's own Submit gate uses;
 *  (d) a partially-filled address blocks, per action, with the existing
 *      message. Called on the ACTION with the declared records key: the
 *      unparameterised call reads a `mortgagees` key the reshaped action may not
 *      carry, returns null, and never blocks;
 *  (e) an unresolved ROLE is a form-level block naming it — the form cannot ask
 *      for a value it has no declared key for, so launching would silently drop
 *      it.
 */
export function validateLaunch({
  binding,
  runLevelValues,
  actions,
  policyOptions,
}: ValidateLaunchArgs): LaunchValidation {
  const fieldErrors: Record<string, string> = {};
  const actionErrors: Array<Record<string, string>> = actions.map(() => ({}));

  // (e) first: an unresolved role makes every other verdict unreliable.
  if (binding.missingRoles.length > 0) {
    return {
      fieldErrors,
      actionErrors,
      blockingReason:
        "The launch contract does not declare a field this form needs: " +
        `${binding.missingRoles.join(", ")}. The form cannot submit a value it ` +
        "has no declared key for.",
    };
  }

  const fields = actionFields(binding);
  const effectiveDateKey = binding.roles.effectiveDate;
  const interestTypeKey = binding.roles.interestType;
  const recordsKey = binding.roles.interestRecords;
  let requiredUnfilled = false;

  actions.forEach((action, index) => {
    const errors = actionErrors[index];
    const flat = mergeLaunchSlice(binding, runLevelValues, action);
    const options = policyOptions ?? readPolicyOptions(flat);
    const policyNumber =
      typeof flat.policy_number === "string" ? flat.policy_number : null;

    // (p) The policy is one of the client's (none resolved: free text stays allowed).
    const notTheClients = validatePolicyMembership(options, policyNumber);
    if (notTheClients) errors.policy_number = notTheClients;

    // (a) Effective date inside the chosen term. The MERGED record is
    // deliberate: `policy_options` and `policy_term_key` stay at run level, so
    // the action slice alone leaves `resolveSelectedPolicy` with no policy
    // number and the validator silently no-ops.
    if (effectiveDateKey) {
      const message = validateEffectiveDateAgainstPolicy(
        flat[effectiveDateKey],
        options,
        policyNumber,
        readPolicyTermKey(flat),
      );
      if (message) errors[effectiveDateKey] = message;
    }

    // (b) Interest-type membership, only when a value is present.
    if (interestTypeKey && binding.interestTypeValues.length > 0) {
      const value = action[interestTypeKey];
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        !binding.interestTypeValues.includes(String(value))
      ) {
        errors[interestTypeKey] =
          `"${String(value)}" isn't one of the interest types this contract ` +
          `declares (${binding.interestTypeValues.join(", ")}).`;
      }
    }

    // (c) Declared required fields on the action slice, plus the required
    // fields of every RECORD ROW the AM actually entered.
    if (!checkAllRequiredFilled(fields, action)) requiredUnfilled = true;
    if (!presentRowsAreComplete(fields, action)) requiredUnfilled = true;

    // (d) Partially-filled address, per action.
    if (recordsKey) {
      const message = mortgageeAddressError(action, recordsKey);
      if (message) errors[recordsKey] = message;
    }
  });

  const blockingReason =
    firstMessage(actionErrors) ??
    (requiredUnfilled
      ? "Fill in every required field before launching."
      : null);

  return { fieldErrors, actionErrors, blockingReason };
}
